import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { useEffect, useRef, useState } from "react";
import { ProfitAndLossMonthlyGraphData } from "@/types/profit-and-loss-monthly-graph-api";
import { useCurrency } from "@/context/CurrencyContext";
import i18n from "@/i18n";
import { formatToShortMonth } from "@/lib/time_utils";

const transformDataToChartData = (graphData: ProfitAndLossMonthlyGraphData) => {
  const { months, income, directCosts, operatingCosts } = graphData;
  const chartData = [];
  const locale = i18n.language;

  for (let i = 0; i < months.length; i++) {
    const monthStr = new Date(months[i]).toLocaleString(locale, {
      month: "short",
    });

    chartData.push({
      month: monthStr,
      income: income.total[i] ?? 0,
      incomeThisYear: income.totalPreviousPeriod[i] ?? 0,
      directCosts: directCosts.total[i] ?? 0,
      directCostsThisYear: directCosts.totalPreviousPeriod[i] ?? 0,
      operatingCosts: operatingCosts.total[i] ?? 0,
      operatingCostsThisYear: operatingCosts.totalPreviousPeriod[i] ?? 0,
    });
  }

  return chartData;
};

const colors = {
  income: "#22c55e",
  directCosts: "#ef4444",
  operatingCosts: "#f59e0b",
};

const MetricChart = ({ data, metricType, startYear, endYear }) => {
  const chartData = transformDataToChartData(data);
  const [chartHeight, setChartHeight] = useState(400);
  const containerRef = useRef(null);
  const colorDate = colors[metricType] || "#999";
  const colorText = "#000";
  const { formatNumber } = useCurrency();

  useEffect(() => {
    const calculateHeight = () => {
      const topOffset = containerRef.current?.getBoundingClientRect().top || 0;
      const windowHeight = window.innerHeight;
      const reservedSpace = 100;
      const availableHeight = windowHeight - topOffset - reservedSpace;
      setChartHeight(Math.max(300, availableHeight));
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  return (
    <div ref={containerRef} style={{ height: chartHeight }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 40, right: 30, left: 20, bottom: 0 }}
        >
          <defs>
            <linearGradient
              id={`color-${metricType}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="5%" stopColor={colorDate} stopOpacity={0.2} />
              <stop offset="95%" stopColor={colorDate} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="month" tickFormatter={formatToShortMonth} />
          <YAxis tickFormatter={(value) => formatNumber(Number(value))} />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            formatter={(value: number) => formatNumber(value)}
            labelFormatter={(label) => `${formatToShortMonth(label)}`}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey={metricType}
            stroke={colorDate}
            fillOpacity={1}
            fill={`url(#color-${metricType})`}
            name={endYear}
          />
          <Area
            type="monotone"
            dataKey={`${metricType}ThisYear`}
            stroke={colorText}
            strokeDasharray="4 4"
            fillOpacity={0}
            name={startYear}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MetricChart;
